import { change, resetSection, FormSection } from 'redux-form';
import connect from 'components/Shared/connect';
import { IconArrow } from 'components/Shared/Icon';
import { CustomerType } from 'models/CustomerType';
import batchAction from 'store/batching';
import { setSiteMode } from 'store/appShell/actions';
import { customerTypeToSiteMode } from 'components/SiteMode';
import { hideDeliveryOptions, quickStartSet, onBlurCheckout } from 'views/CheckoutPage/actions';
import { checkoutForm } from 'views/CheckoutPage/selector';
import { Button } from 'components/Button';
import { rm } from 'utils';
import { ContactInfoPrivate } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/Person';
import { ContactInfoCompany } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/Company';
import { SectionTitle } from 'views/CheckoutPage/SectionTitle';
import styles from './styles.scss';
import {
  TooLongNameCompanyModal,
  TooLongNamePersonModal
} from 'views/CheckoutPage/ContactInfo/TooLongNameModal';
import { useSelector } from 'react-redux';
import {
  selectAddressInfo,
  selectNameMaxLength
} from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/selector';
import { getQaDataAttr } from '@bygghemmait/utilities';

interface MergedProps {
  customerType: CustomerType;
  toggleCustomerType: () => void;
  resetForm: (quickStart: boolean) => void;
}

const _ContactInfo = ({ customerType, toggleCustomerType, resetForm }: MergedProps) => {
  const address = useSelector(selectAddressInfo);
  const maxNameLength = useSelector(selectNameMaxLength);
  const isCompanyMode = customerType === CustomerType.Company;

  const title = rm('checkout_contact_title');
  const modeToggleLabel = isCompanyMode ? rm('checkout_private') : rm('checkout_company');
  const ModeToggle = () => (
    <Button
      onMouseDown={toggleCustomerType}
      appearance="link"
      className={styles.modeToggle}
      type="button"
      {...getQaDataAttr(isCompanyMode ? 'b2c-switch' : 'b2b-switch')}
    >
      {modeToggleLabel}
      <IconArrow className={styles.icon} />
    </Button>
  );

  const content = isCompanyMode ? (
    <ContactInfoCompany reset={resetForm} />
  ) : (
    <ContactInfoPrivate reset={resetForm} />
  );

  const TooLongNameModal = isCompanyMode ? TooLongNameCompanyModal : TooLongNamePersonModal;

  return (
    <>
      <FormSection name="address">
        <div className={styles.contactContainer}>
          <div className={styles.header}>
            <SectionTitle title={title} modeToggle={<ModeToggle />} />
          </div>
          {content}
        </div>
      </FormSection>
      {address && maxNameLength && address.isTooLongName && (
        <TooLongNameModal address={address} maxNameLength={maxNameLength} />
      )}
    </>
  );
};

interface StateProps {
  _toggleCustomerType: (
    customerType: CustomerType,
    shouldChangeSiteMode: boolean,
    quickStart: boolean
  ) => void;
  _shouldChangeSiteMode: boolean;
}

const ContactInfo = connect<MergedProps, Record<string, unknown>, StateProps>(
  state => ({
    customerType: checkoutForm(state, 'address.customerType') as CustomerType,
    _shouldChangeSiteMode: state.appShell.channel === 'GOP'
  }),
  dispatch => ({
    _toggleCustomerType: (customerType, shouldChangeSiteMode, quickStart) => {
      const actions = [
        resetSection('checkout', 'address'),
        change('checkout', 'address', { customerType }),
        hideDeliveryOptions(),
        quickStartSet({ value: quickStart }),
        onBlurCheckout('address.customerType')
      ];

      if (shouldChangeSiteMode) {
        actions.push(setSiteMode(customerTypeToSiteMode(customerType), false));
      }

      return dispatch(batchAction(...actions));
    }
  }),
  ({ _shouldChangeSiteMode, ...state }, { _toggleCustomerType, ...dispatch }, props) => ({
    ...state,
    ...dispatch,
    ...props,
    toggleCustomerType: () => {
      const newCustomerType =
        state.customerType === CustomerType.Company ? CustomerType.Private : CustomerType.Company;

      _toggleCustomerType(newCustomerType, _shouldChangeSiteMode, true);
    },
    resetForm: quickStart => _toggleCustomerType(state.customerType, false, quickStart)
  })
)(_ContactInfo);

export default ContactInfo;
